import { Controller } from "@hotwired/stimulus";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Scroll-reveals its items: they fade and rise into place as they enter the
// viewport, lightly staggered. The content is visible by default (in CSS), so
// without JS, or when the visitor prefers reduced motion, nothing is ever
// hidden — this controller only ADDS motion, it never gates content. (gsap.from
// sets the hidden start state itself when motion is allowed.)
export default class extends Controller {
  static targets = ["item"];
  static values = {
    y: { type: Number, default: 24 },
    duration: { type: Number, default: 0.6 },
    stagger: { type: Number, default: 0.12 },
  };

  connect() {
    // Turbo shows a cached preview before the real response; skip it so the
    // reveal only runs once, on the real page (matches impression_controller).
    if (document.documentElement.hasAttribute("data-turbo-preview")) {return;}

    const items = this.hasItemTarget ? this.itemTargets : [this.element];

    // Reduced motion (or no items): leave everything visible, just mark ready.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.markReady();
      return;
    }

    this.tween = gsap.from(items, {
      opacity: 0,
      y: this.yValue,
      duration: this.durationValue,
      stagger: this.staggerValue,
      ease: "power2.out",
      scrollTrigger: { trigger: this.element, start: "top 85%", once: true },
      // Guarantee the end state even if the controller is torn down mid-tween
      // (Turbo navigation), so content can never be left faded out.
      onInterrupt: () => gsap.set(items, { opacity: 1, y: 0 }),
    });

    this.markReady();
  }

  disconnect() {
    this.tween?.scrollTrigger?.kill();
    this.tween?.kill();
    this.tween = null;
  }

  // Lets system specs wait until the controller has wired up.
  markReady() {
    this.element.setAttribute("data-reveal-ready", "");
  }
}
